import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LuCheck } from "react-icons/lu";
import { Products } from "./types";
import { addToCart, selectCartLines, setQuantity } from "../../../Redux/reducer/cartReducer";
import { ORDERING_ENABLED } from "../../../config/features";
import { exGst, DEFAULT_GST_PERCENT } from "../../../utils/priceText";
import { CiButton, CiTag, QtyStepper, RateLine } from "../../../common/ci/Ui";
import { useCiT } from "../../../common/ci/i18n";
import defaultImage from "../../../assets/defaultImage.svg";

/**
 * Listing ki ek row — design canvas "2 · Search / Listing" (table, cards nahi):
 * image · naam + brand + badge · rate ex-GST + GST · qty stepper · Add.
 * Rate purane highLowPrice (GST-incl.) se exGst(); API ka gstPercent aaye to wahi.
 */
const ProductRow = ({ data }: { data: Products & { gstPercent?: number; taxableValue?: number } }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useCiT();
  const lines = useSelector(selectCartLines);
  const inCart = lines.find((l) => l.companyProductId === data.id);
  const [qty, setQty] = useState<number>(inCart ? inCart.quantity : 1);

  // qty ek baar cart se copy hokar local state me baith jaati thi. Cart page
  // ya kisi doosri row se wahi item badalne par ye stepper purana number
  // dikhata rehta tha. Cart ki value bahar se badle tabhi sync karo — user ke
  // type kiye number ko mat chheno.
  const cartQty = inCart?.quantity;
  const lastCartQty = useRef(cartQty);
  useEffect(() => {
    if (cartQty !== lastCartQty.current) {
      lastCartQty.current = cartQty;
      if (cartQty !== undefined) setQty(cartQty);
    }
  }, [cartQty]);

  const image = Array.isArray(data.images) ? data.images[0] : data.images;
  const companyImage = Array.isArray(data.companyImage) ? data.companyImage[0] : data.companyImage;
  const gst = Number.isFinite(Number(data.gstPercent)) ? Number(data.gstPercent) : DEFAULT_GST_PERCENT;
  const low = data.highLowPrice?.lowFinalPrice ?? data.finalPrice;
  const rate =
    Number(data.taxableValue) > 0 ? Number(data.taxableValue) : exGst(low, gst);
  const priced = Number.isFinite(rate) && rate > 0;

  const href = `/product/${data.id}/${encodeURIComponent(data.productName || "item")}`;
  const open = () => navigate(href);

  const add = () => {
    if (!ORDERING_ENABLED) return;
    if (inCart) {
      dispatch(setQuantity({ companyProductId: data.id, quantity: qty }));
      toast.success(t("Cart update ho gaya"));
      return;
    }
    dispatch(
      addToCart({
        companyProductId: data.id,
        quantity: qty > 0 ? qty : 1,
        itemName: data.productName,
        companyName: data.companyName,
        itemCode: data.itemCode ? String(data.itemCode) : undefined,
        unit: data.unit,
        image: typeof image === "string" ? image : undefined,
      })
    );
    toast.success(t("Cart me add ho gaya"));
  };

  return (
    <div className="grid grid-cols-1 gap-3 border-b border-ci-line bg-white px-3 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_150px_170px_100px] sm:items-center sm:gap-3 sm:px-4">
      <div className="flex items-center gap-3">
        <Link to={href} className="h-[56px] w-[72px] shrink-0 overflow-hidden rounded-md bg-[#eceae5]">
          <img src={(typeof image === "string" && image) || defaultImage} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        </Link>
        <div className="flex min-w-0 flex-col gap-1">
          <Link to={href} className="truncate text-left text-[15px] font-semibold hover:text-ci-navy">
            {data.productName}
          </Link>
          <span className="flex items-center gap-2 truncate text-[13px] text-ci-muted">
            {companyImage ? <img src={companyImage} alt="" className="h-4 w-4 rounded-full object-contain" /> : null}
            {[data.companyName, data.itemCode ? `Code ${data.itemCode}` : ""].filter(Boolean).join(" · ")}
          </span>
          <span className="flex gap-1.5">
            {data.status ? <CiTag tone="ok">{t("Available")}</CiTag> : <CiTag tone="muted">{t("Out of stock")}</CiTag>}
            <CiTag tone="navy">{t("Rudrapur godown")}</CiTag>
          </span>
        </div>
      </div>
      <div className="flex items-baseline justify-between sm:block">
        <span className="text-[12px] text-ci-muted sm:hidden">{t("Rate")}</span>
        <RateLine rate={priced ? rate : null} unit={data.unit} gstPercent={gst} size="sm" />
      </div>
      {ORDERING_ENABLED ? (
        <>
          <div className="flex items-center justify-between sm:block">
            <span className="text-[12px] text-ci-muted sm:hidden">{t("Qty")}</span>
            <QtyStepper size="sm" value={qty} unit={data.unit} min={0} onChange={setQty} />
          </div>
          <CiButton size="sm" variant={inCart ? "line" : "navy"} onClick={add} disabled={!data.status && !priced}>
            {inCart ? (<><LuCheck size={16} /> {t("In cart")}</>) : t("Add")}
          </CiButton>
        </>
      ) : (
        <CiButton size="sm" variant="navy" onClick={open} className="sm:col-span-2">{t("View")}</CiButton>
      )}
    </div>
  );
};

export default ProductRow;
